import { Box, Stack, useTheme } from "@mui/material";

import {
  Button,
  ExternalLinkIcon2,
  Input,
  LockIcon,
  LockIcon2,
  ScaleIcon,
  UnLockIcon,
} from "components";
import { Control, useFormContext } from "react-hook-form";
import { ConfigItemsLayout } from "./ConfigItemsLayout";

export const SizeConfigItem = ({ isLoading }: { isLoading: boolean }) => {
  const {
    watch,
    setValue,
    getValues,
    control,
    formState: { errors },
  } = useFormContext();
  const theme = useTheme();
  const isSizeSynced = watch("is_size_synced");
  return (
    <ConfigItemsLayout
      title="Size"
      description="Control the size of your logo."
      icon={<ScaleIcon sx={{ width: 20, height: 20 }} />}
    >
      <Stack direction={"row"} gap="4px">
        <SizeInputField
          control={control}
          name={"width"}
          label={"Width"}
          type={"number"}
          disabled={isLoading}
          error={!!errors?.width?.message}
          errorMessage={errors?.width?.message as string}
          onBlur={() => {
            if (isLoading) {
              return;
            }
            let width = getValues("width");
            width = width < 50 ? 50 : width > 150 ? 150 : width;
            if (isSizeSynced) {
              const oldWidth = getValues("width");
              const oldHeight = getValues("height");
              if (oldWidth && oldHeight) {
                const aspectRatio = Number(oldHeight) / Number(oldWidth);
                setValue("height", width * aspectRatio, {
                  shouldDirty: true,
                });
              }
            }
            setValue("width", width, {
              shouldDirty: true,
            });
          }}
          onChange={(value) => {
            if (isLoading) {
              return;
            }
            let size = Number(value);
            size = size > 150 ? 150 : size;
            if (isSizeSynced) {
              const oldWidth = getValues("width");
              const oldHeight = getValues("height");
              if (oldWidth && oldHeight) {
                const aspectRatio = Number(oldHeight) / Number(oldWidth);
                setValue("height", size * aspectRatio, {
                  shouldDirty: true,
                });
              }
            }
            setValue("width", size, {
              shouldDirty: true,
            });
          }}
        />
        <Button
          variant="ghost"
          isIconOnly
          disabled={isLoading}
          icon={
            isSizeSynced ? (
              <LockIcon
                sx={{
                  width: "22px",
                  height: "22px",
                  color: theme.palette.lightBg.main,
                }}
              />
            ) : (
              <UnLockIcon
                sx={{
                  width: "22px",
                  height: "22px",
                  color: theme.palette.lightBg.main,
                }}
              />
            )
          }
          onClick={() => {
            if (isLoading) {
              return;
            }
            setValue("is_size_synced", !isSizeSynced);
          }}
        />
        <SizeInputField
          control={control}
          name={"height"}
          label={"Height"}
          type={"number"}
          onBlur={() => {
            if (isLoading) {
              return;
            }
            let height = getValues("height");
            height = height < 30 ? 30 : height > 100 ? 100 : height;
            if (isSizeSynced) {
              const oldWidth = getValues("width");
              const oldHeight = getValues("height");
              if (oldWidth && oldHeight) {
                const aspectRatio = Number(oldWidth) / Number(oldHeight);
                setValue("width", height * aspectRatio, {
                  shouldDirty: true,
                });
              }
            }
            setValue("height", height, {
              shouldDirty: true,
            });
          }}
          disabled={isLoading}
          error={!!errors?.height?.message}
          errorMessage={errors?.height?.message as string}
          onChange={(value) => {
            if (isLoading) {
              return;
            }
            let size = Number(value);
            size = size > 100 ? 100 : size;
            if (isSizeSynced) {
              const oldWidth = getValues("width");
              const oldHeight = getValues("height");
              if (oldWidth && oldHeight) {
                const aspectRatio = Number(oldWidth) / Number(oldHeight);
                setValue("width", size * aspectRatio, {
                  shouldDirty: true,
                });
              }
            }
            setValue("height", size, {
              shouldDirty: true,
            });
          }}
        />
      </Stack>
    </ConfigItemsLayout>
  );
};

const SizeInputField = ({
  control,
  name,
  label,
  type,
  error,
  errorMessage,
  onChange,
  disabled,
  onBlur,
}: {
  control: Control;
  name: string;
  label: string;
  type: string;
  error: boolean;
  errorMessage: string;
  onChange: (value: string) => void;
  disabled: boolean;
  onBlur: () => void;
}) => {
  const theme = useTheme();
  return (
    <Input
      control={control}
      name={name}
      label={label}
      type={type}
      fullWidth={false}
      placeholder={"100"}
      disabled={disabled}
      onBlur={onBlur}
      inputProps={{
        min: 0,
      }}
      sx={{
        width: "120px",
        "& .MuiOutlinedInput-root": {
          padding: "0px 0px",
          "& .MuiInputBase-input": {
            padding: "0px 2px 0px 12px",
          },
        },
      }}
      InputProps={{
        endAdornment: (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: theme.palette.neutral["010"],
              p: "8px",
            }}
          >
            px
          </Box>
        ),
      }}
      error={error}
      errorMessage={errorMessage}
      onKeyDown={(e) => {
        if (e.key === "e" || e.key === "E" || e.key === "+" || e.key === "-") {
          e.preventDefault();
        }
      }}
      onChange={(e) => {
        const value = e.target.value;
        onChange(value);
      }}
    />
  );
};
